import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { isTestEnv } from '@moodys/cre-cpm.functions.environment';
import { ApisauceInstance, create } from 'apisauce';
import { ApiConfig, ApiHeaders } from './models/config';
import { getDefaultConfig } from './config/default';
import { requestAxiosInteceptor, responseAxiosInterceptor } from './functions';

const DEFAULT_API_CONFIG = getDefaultConfig();

class Api {
  private userId: string;

  private companyRefUuid: string;

  protected config: ApiConfig;

  protected token: string;

  protected request: ApisauceInstance;

  protected getHeaders(apiHeaders?: ApiHeaders): Record<string, string> {
    const headers: Record<string, string> = {
      Authorization: apiHeaders?.authorization ?? `Bearer ${this.token}`,
    };
    if (apiHeaders?.contentType) {
      headers['Content-Type'] = apiHeaders.contentType;
    }

    headers['X-User-Id'] = this.userId;
    headers['X-Company-Ref-Uuid'] = this.companyRefUuid;

    return headers;
  }

  constructor(config: ApiConfig = DEFAULT_API_CONFIG) {
    this.config = config;

    const axiosRequest = axios.create({
      baseURL: this.config.url,
      timeout: this.config.timeout,
    });

    this.request = isTestEnv()
      ? (axiosRequest as unknown as ApisauceInstance)
      : create({ baseURL: this.config.url, axiosInstance: axiosRequest });

    this.setToken(config.token);

    axiosRequest.interceptors.response.use(
      (response: any) => response,
      responseAxiosInterceptor(config.disableRedirectOnUnauthorized)
    );

    axiosRequest.interceptors.request.use(requestAxiosInteceptor);
  }

  private setAuthHeaders(): void {
    if (!this.token) {
      this.userId = '';
      this.companyRefUuid = '';
      return;
    }
    try {
      const payload = jwtDecode<{
        user: { user_id: number };
        company: { company_ref_uuid: string };
      }>(this.token) 
      this.userId = `${payload?.user?.user_id ?? ''}`;
      this.companyRefUuid = payload?.company?.company_ref_uuid ?? '';
    } catch {
      // Can't decode token, so don't set headers
    }
  }

  public setToken(newToken: string): void {
    this.token = newToken;
    this.setAuthHeaders();
  }

  public hasToken() {
    return !!this.token;
  }

  public clearToken() {
    this.token = '';
    this.setAuthHeaders();
  }
}

export { Api };
